"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState } from "react";
import { cx } from "../internal/cx.js";
import { IconLoader } from "../internal/icons.js";
import { Ripple, startRipple } from "../internal/ripple.js";
import { Counter } from "./Badge.js";
import { BUSY_AFTER_MS } from "../uxLaws.js";
import { plainClick } from "../internal/navigate.js";
const BUTTON_ALIAS = {
    filled: { m3: "filled", legacy: "primary" }, primary: { m3: "filled", legacy: "primary" },
    outlined: { m3: "outlined", legacy: "secondary" }, secondary: { m3: "outlined", legacy: "secondary" },
    text: { m3: "text", legacy: "quiet" }, quiet: { m3: "text", legacy: "quiet" },
    tonal: { m3: "tonal" }, danger: { m3: "danger" },
};
/** Both the Material 3 class and the old one (`jui-btn--filled jui-btn--primary`), so host selectors keep working. */
const classes = (variant, size, extra) => {
    const { m3, legacy } = BUTTON_ALIAS[variant] ?? BUTTON_ALIAS.secondary;
    return cx("jui-btn", `jui-btn--${m3}`, legacy && `jui-btn--${legacy}`, size === "sm" && "jui-btn--sm", extra);
};
function withRipple(handler) {
    return (event) => { handler?.(event); if (!event.defaultPrevented)
        startRipple(event); };
}
export { BUSY_AFTER_MS };
/**
 * An `onClick` that returns a promise is a long action (facts model ux_laws, П334): while it runs, the button ignores
 * further clicks; past 400 ms it shows «busy» itself — the screen does not have to pass `busy`.
 */
function usePending(onClick) {
    // Shared by Button, IconButton and CountFilter: one rule for every pressable thing.
    const running = useRef(false);
    const [late, setLate] = useState(false);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);
    const click = (event) => {
        if (running.current) {
            event.preventDefault();
            return;
        }
        const result = onClick?.(event);
        if (!result || typeof result.then !== "function")
            return;
        running.current = true;
        const timer = setTimeout(() => { if (alive.current)
            setLate(true); }, BUSY_AFTER_MS);
        const done = () => { clearTimeout(timer); running.current = false; if (alive.current)
            setLate(false); };
        result.then(done, done);
    };
    return { click, late };
}
/** One button for every action: Material 3 filled / tonal / outlined / text / danger; pill shape, state layers, ripple. */
export const Button = forwardRef(function Button({ variant = "secondary", size = "md", icon, iconEnd, busy = false, className, children, type = "button", disabled, onPointerDown, onClick, ...rest }, ref) {
    const pending = usePending(onClick);
    const shown = busy || pending.late;
    return _jsxs("button", { ref: ref, type: type, className: classes(variant, size, className), disabled: disabled || shown, "aria-busy": shown || undefined, onPointerDown: withRipple(onPointerDown), onClick: pending.click, ...rest, children: [_jsx(Ripple, {}), shown ? _jsx(IconLoader, { className: "jui-spin" }) : icon, children, iconEnd] });
});
/** A link that looks like a Button (navigation, downloads). */
export const ButtonLink = forwardRef(function ButtonLink({ variant = "secondary", size = "md", icon, iconEnd, busy: _busy, className, children, onPointerDown, ...rest }, ref) {
    return _jsxs("a", { ref: ref, className: classes(variant, size, className), onPointerDown: withRipple(onPointerDown), ...rest, children: [_jsx(Ripple, {}), icon, children, iconEnd] });
});
const ICON_ALIAS = {
    standard: { m3: "standard", legacy: "quiet" }, quiet: { m3: "standard", legacy: "quiet" },
    filled: { m3: "filled", legacy: "primary" }, primary: { m3: "filled", legacy: "primary" },
    outlined: { m3: "outlined", legacy: "secondary" }, secondary: { m3: "outlined", legacy: "secondary" },
    tonal: { m3: "tonal" }, danger: { m3: "danger" },
};
const iconClasses = (variant, size, pressed, badge, className) => {
    const { m3, legacy } = ICON_ALIAS[variant] ?? ICON_ALIAS.standard;
    return cx("jui-btn", "jui-btn--icon", `jui-btn--${m3}`, legacy && `jui-btn--${legacy}`, size === "sm" && "jui-btn--sm", pressed !== undefined && "jui-btn--toggle", badge ? "jui-btn--with-badge" : undefined, className);
};
/**
 * Round icon-only button with a required label. An `onClick` that returns a promise is a long action, as on Button:
 * no second run while it goes, «busy» after 400 ms. With `href` it is a link inside the app (see `onNavigate`).
 */
export const IconButton = forwardRef(function IconButton({ label, icon, variant = "quiet", size = "md", pressed, badge, badgeTone = "accent", busy = false, className, type = "button", title, disabled, onPointerDown, onClick, href, onNavigate, target, ...rest }, ref) {
    const pending = usePending(onClick);
    const shown = busy || pending.late;
    const classes = iconClasses(variant, size, pressed, badge, className);
    const inner = _jsxs(_Fragment, { children: [_jsx(Ripple, {}), shown ? _jsx(IconLoader, { className: "jui-spin" }) : icon, !!badge && _jsx("span", { className: "jui-btn-badge", children: _jsx(Counter, { value: badge, tone: badgeTone, strong: true }) })] });
    if (href !== undefined) {
        const link = rest;
        return _jsx("a", { ref: ref, ...link, href: disabled ? undefined : href, target: target, rel: target === "_blank" ? "noopener noreferrer" : link.rel, "aria-label": label, title: title ?? label, "aria-disabled": disabled || undefined, className: classes, onPointerDown: withRipple(onPointerDown), onClick: event => {
                if (disabled) {
                    event.preventDefault();
                    return;
                }
                onClick?.(event);
                if (!event.defaultPrevented && onNavigate && !target && plainClick(event)) {
                    event.preventDefault();
                    onNavigate(href);
                }
            }, children: inner });
    }
    return _jsx("button", { ref: ref, type: type, "aria-label": label, title: title ?? label, "aria-pressed": pressed, className: classes, disabled: disabled || shown, "aria-busy": shown || undefined, onPointerDown: withRipple(onPointerDown), onClick: pending.click, ...rest, children: inner });
});
/** A row of IconButtons on a tonal strip (toolbars). `divider` splits groups. */
export function ButtonGroup({ children, label, className }) {
    return _jsx("div", { role: "group", "aria-label": label, className: cx("jui-btn-group", className), children: children });
}
export const ButtonGroupDivider = () => _jsx("span", { className: "jui-btn-group-divider", "aria-hidden": "true" });
/** A filter «icon number» in a row: a toggle (aria-pressed); pressed — tonal, otherwise text. Icon and number are always visible, zero too. */
export function CountFilter({ icon, count, label, pressed, onClick, title, disabled, className }) {
    return _jsx(Button, { size: "sm", variant: pressed ? "tonal" : "quiet", icon: icon, "aria-pressed": pressed, "aria-label": `${label}: ${count}`, title: title ?? `${label}: ${count}`, disabled: disabled, className: cx("jui-count-filter", className), onClick: onClick, children: count });
}
